/**
 * `TelemetrySettings` — GVW-152 telemetry / diagnostics opt-in.
 *
 * Per brief §2 / §7: privacy opt-in/out toggle. Defence-variant builds lock
 * the toggle off per `screen-inventory.md` line 2469 / a11y-plan.md §O 678.
 * Persists via localStorage fallback under `gvw-set-telemetry`.
 *
 * TODO(WP-E11): when `@geosoft/platform-sdk` exposes a real telemetry-consent
 * endpoint, swap `localStorage` for `sdk.telemetry.setConsent(...)` and write
 * the consent log row per brief §13.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-152/brief.md
 */

import { useCallback, useEffect, useState } from 'react';
import { tokens } from '@/design';
import { useDefenceVariant } from '@/design/hooks';
import { useLiveRegion } from '@/state/liveRegion';

interface TelemetryPrefs {
  enabled: boolean;
}

const STORAGE_KEY = 'gvw-set-telemetry';
const DEFAULTS: TelemetryPrefs = { enabled: false };

export function TelemetrySettings() {
  const liveRegion = useLiveRegion();
  const defenceVariant = useDefenceVariant();
  const [settings, setSettings] = useState<TelemetryPrefs>(DEFAULTS);
  const [saved, setSaved] = useState(false);

  const isLocked = defenceVariant.active;

  useEffect(() => {
    if (isLocked) {
      setSettings({ enabled: false });
      return;
    }
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) setSettings(JSON.parse(stored) as TelemetryPrefs);
    } catch {
      /* defaults */
    }
  }, [isLocked]);

  useEffect(() => {
    document.title = 'Telemetry · GeoViewer';
    liveRegion.announce('status', 'Telemetry preferences loaded.');
  }, [liveRegion]);

  const handleSave = useCallback(() => {
    if (isLocked) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      setSaved(true);
      liveRegion.announce('status', `Telemetry ${settings.enabled ? 'enabled' : 'disabled'}.`);
    } catch {
      liveRegion.announce('alert', 'Could not save telemetry settings.');
    }
  }, [settings, isLocked, liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="gvw-152-heading"
      data-gvw-id="GVW-152"
      data-route="/workspace/settings/telemetry"
      style={{
        minHeight: '100%',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <h1
        id="gvw-152-heading"
        style={{
          margin: 0,
          marginBottom: tokens.space[4],
          fontSize: tokens.font.size['2xl'],
          fontWeight: tokens.font.weight.semibold,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[3],
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 28 }}>
          monitoring
        </span>
        Telemetry preferences
      </h1>

      {isLocked ? (
        <div
          role="status"
          id="gvw-152-locked-help"
          style={{
            padding: tokens.space[3],
            marginBottom: tokens.space[4],
            background: tokens.color.surface[1],
            borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.warning}`,
            borderRadius: tokens.radius.md,
            display: 'flex',
            alignItems: 'flex-start',
            gap: tokens.space[3],
          }}
        >
          <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18, color: tokens.color.semantic.warning }}>
            lock
          </span>
          <div>
            <div style={{ fontWeight: tokens.font.weight.medium }}>Telemetry is disabled in defence variant</div>
            <div style={{ fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary }}>
              Defence-variant deployments have telemetry permanently disabled for compliance.
            </div>
          </div>
        </div>
      ) : null}

      {saved ? (
        <div
          role="status"
          style={{
            padding: tokens.space[3],
            marginBottom: tokens.space[4],
            background: tokens.color.surface[1],
            borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.success}`,
            borderRadius: tokens.radius.md,
          }}
        >
          Telemetry preferences saved.
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[5], maxWidth: 640 }}
      >
        <label style={{ display: 'flex', gap: tokens.space[3], alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            role="switch"
            aria-checked={settings.enabled}
            aria-disabled={isLocked}
            aria-describedby={isLocked ? 'gvw-152-locked-help' : 'gvw-152-help'}
            disabled={isLocked}
            checked={settings.enabled}
            onChange={(e) => setSettings({ enabled: e.target.checked })}
            style={{ marginTop: tokens.space[1] }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium }}>
              Enable diagnostic data collection
            </div>
            <div id="gvw-152-help" style={{ fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary, marginTop: tokens.space[1] }}>
              Anonymous performance metrics, feature usage, and crash reports.
              Workspace data, layer content, credentials, and personal information are NOT collected.
            </div>
          </div>
        </label>

        {!isLocked ? (
          <div>
            <button
              type="submit"
              style={{
                padding: `${tokens.space[3]}px ${tokens.space[5]}px`,
                background: tokens.color.accent.primary,
                color: tokens.color.accent.on,
                border: 'none',
                borderRadius: tokens.radius.md,
                fontSize: tokens.font.size.md,
                fontWeight: tokens.font.weight.medium,
                cursor: 'pointer',
              }}
            >
              Save preferences
            </button>
          </div>
        ) : null}
      </form>
    </main>
  );
}
